import type { FilterField } from '../../components/new-design/vyTableFilter';
import { parseJson, vySerialize } from '@vy/utils';
import { useCallback, useMemo } from 'react';
import { useStateParams } from '../use-state-params';

type FilterWithField = {
  field: string;
  value: unknown;
};

export function useFiltersParam<FilterType extends FilterWithField>(
  paramName: string,
  filterFields?: FilterField[],
) {
  const fieldInputTypeMap = useMemo(() => {
    if (!filterFields) {
      return new Map<string, string>();
    }

    return new Map(filterFields.map((field) => [field.id, field.inputType ?? 'text']));
  }, [filterFields]);

  const getFieldInputType = useCallback(
    (fieldId: string): string => {
      return fieldInputTypeMap.get(fieldId) ?? 'text';
    },
    [fieldInputTypeMap],
  );

  const serializeFilters = useCallback(
    (filters: FilterType[]): string => {
      if (!filterFields || filterFields.length === 0) {
        return vySerialize(filters);
      }

      const transformedFilters = filters.map((filter) => {
        const fieldInputType = getFieldInputType(filter.field);

        if (fieldInputType === 'boolean' && Array.isArray(filter.value) && filter.value.length > 0) {
          const firstValue = filter.value[0];

          if (typeof firstValue === 'string' && (firstValue === 'true' || firstValue === 'false')) {
            return { ...filter, value: firstValue === 'true' };
          }
        }

        return filter;
      });

      return vySerialize(transformedFilters);
    },
    [filterFields, getFieldInputType],
  );

  const deserializeFilters = useCallback(
    (filtersString: string): FilterType[] => {
      try {
        if (!filtersString) {
          return [];
        }

        const parsed = parseJson<FilterType[]>(filtersString);

        return parsed ?? [];
      } catch (error) {
        console.warn('Failed to deserialize filters:', error);

        return [];
      }
    },
    [],
  );

  return useStateParams<FilterType[]>([], paramName, serializeFilters, deserializeFilters);
}